import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { slug } from "./utils";

export const FieldBlock = ({ step, label, hint, error, htmlFor, children, testId }) => (
    <div data-testid={testId} className="py-7 first:pt-0 border-b border-hire-line last:border-b-0">
        <div className="flex items-baseline gap-3">
            {step && (
                <span className="text-[11px] font-bold tracking-[0.12em] text-brand-blue">
                    {step}
                </span>
            )}
            <label
                htmlFor={htmlFor}
                className="text-[15px] md:text-base font-semibold text-hire-ink"
            >
                {label}
            </label>
            {hint && <span className="text-xs text-hire-muted">{hint}</span>}
        </div>
        <div className="mt-4">{children}</div>
        {error && (
            <p role="alert" data-testid={`${testId}-error`} className="mt-2.5 text-[13px] text-[#DC2626]">
                {error}
            </p>
        )}
    </div>
);

export const ChipGroup = ({ options, value, onChange, multiple = false, name, label }) => {
    const isSelected = (opt) => (multiple ? value.includes(opt) : value === opt);
    const toggle = (opt) => {
        if (!multiple) return onChange(opt);
        onChange(isSelected(opt) ? value.filter((v) => v !== opt) : [...value, opt]);
    };
    return (
        <div role={multiple ? "group" : "radiogroup"} aria-label={label} className="flex flex-wrap gap-2.5">
            {options.map((opt) => {
                const on = isSelected(opt);
                return (
                    <button
                        key={opt}
                        type="button"
                        role={multiple ? "checkbox" : "radio"}
                        aria-checked={on}
                        data-testid={`chip-${name}-${slug(opt)}`}
                        data-selected={on}
                        onClick={() => toggle(opt)}
                        className={cn(
                            "inline-flex items-center gap-2 h-11 px-4 rounded-full border text-sm font-medium",
                            "transition-[background-color,border-color,color,transform] duration-200 active:scale-[0.98]",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2",
                            on
                                ? "bg-brand-blue border-brand-blue text-white"
                                : "bg-white border-hire-line text-hire-ink hover:border-hire-ink/40"
                        )}
                    >
                        {multiple && (
                            <span
                                className={cn(
                                    "h-4 w-4 rounded-full border grid place-items-center transition-colors",
                                    on ? "bg-white border-white text-brand-blue" : "border-hire-ink/30"
                                )}
                            >
                                {on && <Check className="h-3 w-3" strokeWidth={3} />}
                            </span>
                        )}
                        {opt}
                    </button>
                );
            })}
        </div>
    );
};

export const Segmented = ({ options, value, onChange, name, label }) => (
    <div
        role="radiogroup"
        aria-label={label}
        className="flex flex-wrap sm:inline-flex w-full sm:w-auto gap-1 p-1 rounded-xl bg-hire-bg border border-hire-line"
    >
        {options.map((opt) => {
            const o = typeof opt === "string" ? { value: opt, label: opt } : opt;
            const on = value === o.value;
            return (
                <button
                    key={o.value}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    data-testid={`seg-${name}-${slug(o.value)}`}
                    data-selected={on}
                    onClick={() => onChange(o.value)}
                    className={cn(
                        "flex-1 basis-[calc(50%-4px)] sm:basis-auto sm:flex-none min-h-10 py-2 px-3 sm:px-5 rounded-lg text-[13px] sm:text-sm font-medium leading-tight",
                        "transition-[background-color,color,box-shadow] duration-200",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue",
                        on
                            ? "bg-white text-hire-ink shadow-[0_1px_3px_rgba(11,15,25,0.12)]"
                            : "text-hire-muted hover:text-hire-ink"
                    )}
                >
                    {o.label}
                </button>
            );
        })}
    </div>
);

export const inputClass = (error) =>
    cn(
        "w-full h-12 rounded-xl border bg-white px-4 text-[15px] text-hire-ink placeholder:text-hire-muted/70",
        "transition-[border-color,box-shadow] duration-200 focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue",
        error ? "border-[#DC2626]" : "border-hire-line hover:border-hire-ink/30"
    );
